import { UserX } from "lucide-react";
import { KitModal } from "./kit/Modal";
import { KitButton } from "./kit/Button";
import type { DeepLinkMismatch } from "@/hooks/useWorkspaceDeepLink";
import { useCoShellPortal } from "../CoShellPortalContext";

/**
 * An emailed link was opened while signed in as someone other than the person
 * it was sent to (`expectedUserId`, see useWorkspaceDeepLink). Not a security
 * gate — the server decides access as always — just a heads-up, with a way to
 * sign in as the right account and land back on the same link.
 */
export default function WrongAccountModal({
  mismatch,
  onDismiss,
  onSignOut,
}: {
  mismatch: DeepLinkMismatch | null;
  onDismiss: () => void;
  onSignOut: () => void;
}) {
  // The mismatch is known on the shell's first render, before .co-shell (the
  // portal target) exists. Opening then mounts the overlay in <body>, React
  // Aria hides everything else, and the modal is then moved INTO that hidden
  // shell — every click passes through it and reads as "outside", dismissing
  // it. So wait for the portal.
  const portal = useCoShellPortal();
  return (
    <KitModal
      isOpen={!!mismatch && !!portal}
      onOpenChange={(open) => !open && onDismiss()}
      icon={UserX}
      title="This link wasn't meant for this account"
      description={`It was sent to a different account than the one you're signed in with${
        mismatch ? ` (${mismatch.signedInAs})` : ""
      }. Sign in with the account the email was sent to, or carry on as you are.`}
      modalClassName="max-w-[480px]"
    >
      <div className="flex flex-wrap justify-end gap-2">
        <KitButton variant="secondary" onClick={onDismiss}>
          Stay signed in
        </KitButton>
        <KitButton onClick={onSignOut}>Sign in as someone else</KitButton>
      </div>
    </KitModal>
  );
}
